import { ClaimVerification } from "@/types/summary";
import { findAllOccurrences } from "@/utils/textMatching";

export interface ClaimEvidenceMapping {
  claimIndex: number;
  claim: ClaimVerification;
  paragraphIndices: Set<number>;
  // Map from paragraph index to evidence text that appears in that paragraph
  evidenceByParagraph: Map<number, string[]>;
}

/**
 * Maps claims to manuscript paragraphs by finding where evidence text appears.
 * Uses fuzzy text matching to locate evidence strings within paragraphs.
 * Handles evidence that spans multiple paragraphs.
 */
export function mapClaimsToManuscript(
  claims: ClaimVerification[],
  manuscriptParagraphs: string[],
): ClaimEvidenceMapping[] {
  const mappings: ClaimEvidenceMapping[] = [];

  // Join all paragraphs with a special marker to search across boundaries
  const PARAGRAPH_SEPARATOR = "\n\n";
  const fullText = manuscriptParagraphs.join(PARAGRAPH_SEPARATOR);

  // Build maps for character positions to paragraph indices and positions within paragraphs
  const charToParagraphMap: number[] = new Array(fullText.length);
  const paragraphStarts: number[] = [];
  let currentPos = 0;
  manuscriptParagraphs.forEach((paragraph, paragraphIndex) => {
    paragraphStarts[paragraphIndex] = currentPos;
    const paragraphEnd = currentPos + paragraph.length;
    for (let i = currentPos; i < paragraphEnd; i += 1) {
      charToParagraphMap[i] = paragraphIndex;
    }
    currentPos = paragraphEnd + PARAGRAPH_SEPARATOR.length;
  });

  claims.forEach((claim, claimIndex) => {
    const paragraphIndices = new Set<number>();
    const evidenceByParagraph = new Map<number, string[]>();

    // For each evidence string in this claim
    claim.evidence.forEach((evidenceText) => {
      // Search in the full joined text to find cross-paragraph matches
      const occurrences = findAllOccurrences(fullText, evidenceText);

      occurrences.forEach((occurrence) => {
        // Group consecutive characters by paragraph
        const paragraphRanges = new Map<
          number,
          { start: number; end: number }
        >();

        for (
          let i = occurrence.start;
          i < occurrence.end && i < charToParagraphMap.length;
          i += 1
        ) {
          const paragraphIndex = charToParagraphMap[i];
          if (paragraphIndex !== undefined) {
            if (!paragraphRanges.has(paragraphIndex)) {
              paragraphRanges.set(paragraphIndex, { start: i, end: i + 1 });
            } else {
              paragraphRanges.get(paragraphIndex)!.end = i + 1;
            }
          }
        }

        // For each affected paragraph, extract the portion of evidence text in that paragraph
        paragraphRanges.forEach((range, paragraphIndex) => {
          paragraphIndices.add(paragraphIndex);

          // Extract the substring from fullText that belongs to this paragraph
          const paragraphStart = paragraphStarts[paragraphIndex];
          const localStart = range.start - paragraphStart;
          const localEnd = range.end - paragraphStart;
          const paragraph = manuscriptParagraphs[paragraphIndex];

          // Extract the actual text that appears in this paragraph
          const paragraphEvidenceText = paragraph
            .substring(
              Math.max(0, localStart),
              Math.min(paragraph.length, localEnd),
            )
            .trim();

          if (paragraphEvidenceText) {
            // Store evidence text portion for this paragraph
            const existing = evidenceByParagraph.get(paragraphIndex) || [];
            if (!existing.includes(paragraphEvidenceText)) {
              evidenceByParagraph.set(paragraphIndex, [
                ...existing,
                paragraphEvidenceText,
              ]);
            }
          }
        });
      });
    });

    mappings.push({
      claimIndex,
      claim,
      paragraphIndices,
      evidenceByParagraph,
    });
  });

  return mappings;
}

/**
 * Finds the first paragraph that contains evidence for a claim.
 * Used for scrolling to the claim location.
 */
export function getFirstEvidenceParagraph(
  mapping: ClaimEvidenceMapping,
): number | null {
  if (mapping.paragraphIndices.size === 0) return null;
  return Math.min(...Array.from(mapping.paragraphIndices));
}
